<script setup lang="ts">
/**
 * App.vue — root layout.
 *
 * Responsibilities:
 * - Wait for the initial auth probe (`useAuth().ready`) before rendering
 *   any content so users don't see a flash of the wrong view during the
 *   first navigation.
 * - Provide the persistent sidebar + main-area layout for authenticated
 *   routes. Unauthenticated routes (LoginView) render full-screen — the
 *   sidebar is hidden when `route.meta.public === true` (set in
 *   router.ts) so the login page isn't squeezed.
 * - Host the global `ToastNotification` slot. Toast state is module-
 *   scoped in `useToast`; any composable or view can push a toast and
 *   it renders here.
 *
 * NOT responsible for:
 * - Route-level auth redirection — handled by the router's `beforeEach`
 *   guard in `router.ts`.
 */
import { computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuth, SIGNED_OUT_EVENT } from "./composables/useAuth";
import { useToast } from "./composables/useToast";
import { useTabTitleNotifier } from "./composables/useTabTitleNotifier";
import Sidebar from "./components/Sidebar.vue";
import ToastNotification from "./components/ToastNotification.vue";

const { ready } = useAuth();
const { currentToast, dismiss } = useToast();
const route = useRoute();
const router = useRouter();

const showSidebar = computed(() => route.meta.public !== true);

// Tab-title flash on processing→terminal transitions while the tab
// is unfocused. Single-tab semantics — mount in App.vue (the only
// always-on component) so the listener is registered exactly once.
useTabTitleNotifier();

function onSignedOut() {
  if (router.currentRoute.value.name !== "login") {
    router.replace({ name: "login" });
  }
}
onMounted(() => window.addEventListener(SIGNED_OUT_EVENT, onSignedOut));
onBeforeUnmount(() => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut));
</script>

<template>
  <div v-if="ready" class="min-h-screen flex bg-bg-body text-text-primary">
    <Sidebar v-if="showSidebar" />
    <main class="flex-1 min-w-0">
      <router-view />
    </main>
    <ToastNotification :toast="currentToast" @dismiss="dismiss" />
  </div>
</template>
